// Rendered previews of files: which files have one (Markdown, images),
// repository-relative URL resolution for rendered Markdown, and which
// rendered sections a diff's change blocks touch.
import type { Block } from '../engine/types';

export type PreviewKind = 'markdown' | 'image';

/** Largest file rendered as a preview (bytes, or UTF-16 units of loaded text). */
export const PREVIEW_MAX = 1024 * 1024;

const MARKDOWN_EXT = new Set(['md', 'markdown', 'mdx']);
const IMAGE_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
};

function extension(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** The kind of rendered form a file has, by its extension; null when it has none. */
export function previewKind(path: string | null | undefined): PreviewKind | null {
  if (!path) return null;
  const ext = extension(path);
  if (MARKDOWN_EXT.has(ext)) return 'markdown';
  if (ext in IMAGE_MIME) return 'image';
  return null;
}

/** The media type an image file is shown as (never inlined: always an <img> from [imageSource]). */
export function imageMime(path: string): string | null {
  return IMAGE_MIME[extension(path)] ?? null;
}

/** An image URL for an <img>, and how to free it once the image is gone. */
export interface ImageSource {
  url: string;
  release: () => void;
}

/**
 * The URL an <img> shows a repository image's `bytes` from: the only place
 * the app makes one. An SVG gets a `data:` URL, never a `blob:` one: an SVG
 * from a PR can carry script, and a `blob:` URL opened in its own tab is a
 * document on the app's origin, while browsers refuse to open a `data:` URL
 * as a top-level page. Other images get a `blob:` URL, freed by `release`.
 */
export function imageSource(bytes: Uint8Array, mime: string): ImageSource {
  if (mime === 'image/svg+xml') return { url: `data:${mime};base64,${base64(bytes)}`, release: () => {} };
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
  return { url, release: () => URL.revokeObjectURL(url) };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** "12.3 KB"-style size. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * A link or image URL in a Markdown file, resolved against the file's path
 * in the repository: `{ path, hash }` for a repository path (`./a.png`,
 * `../docs/x.md#usage`, `/README.md`), `{ path: <file>, hash }` for a bare
 * `#fragment`, and null for anything else (a URL with a scheme, `//host`,
 * a path above the repository root, or an empty URL).
 */
export function resolveRepoPath(file: string, url: string): { path: string; hash: string } | null {
  const raw = url.trim();
  if (!raw || /^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//')) return null;
  const hashAt = raw.indexOf('#');
  const hash = hashAt >= 0 ? raw.slice(hashAt) : '';
  let rest = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
  const q = rest.indexOf('?');
  if (q >= 0) rest = rest.slice(0, q);
  if (!rest) return hash ? { path: file, hash } : null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(rest);
  } catch {
    decoded = rest;
  }
  const parts = decoded.startsWith('/') ? [] : file.split('/').slice(0, -1);
  for (const seg of decoded.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (!parts.length) return null;
      parts.pop();
    } else parts.push(seg);
  }
  if (!parts.length) return null;
  return { path: parts.join('/'), hash };
}

/** Encode a repository path for a URL, segment by segment. */
export function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/** A top-level Markdown section: 1-based inclusive source lines. */
export interface LineSpan {
  start: number;
  end: number;
}

/**
 * Lines of each side the change blocks touch: changed lines, plus for a
 * change with no line on that side (a pure deletion seen from the new
 * side) the line just after it, so the section where it happened is marked.
 */
export function changedLines(blocks: Block[], side: 'old' | 'new'): Set<number> {
  const out = new Set<number>();
  const own = side === 'new' ? 'add' : 'del';
  let last = 0;
  for (const b of blocks) {
    let hit = false;
    if (b.kind === 'change') {
      for (const r of b.rows) {
        const n = side === 'new' ? r.new : r.old;
        if (r.kind === own && n !== null) {
          out.add(n);
          hit = true;
        }
      }
      if (!hit && b.rows.some((r) => r.kind !== 'context')) out.add(last + 1);
    }
    for (const r of b.rows) {
      const n = side === 'new' ? r.new : r.old;
      if (n !== null && n > last) last = n;
    }
  }
  return out;
}

/**
 * Which sections a set of changed lines falls in. A line inside a section
 * marks it; a line between sections (a blank line) marks the next section,
 * else the last one.
 */
export function changedSections(sections: LineSpan[], lines: Set<number>): boolean[] {
  const out = sections.map(() => false);
  if (!sections.length) return out;
  for (const n of lines) {
    let i = sections.findIndex((s) => n <= s.end);
    if (i < 0) i = sections.length - 1;
    out[i] = true;
  }
  return out;
}
